import { Component, ViewChild } from '@angular/core';
import { Device } from '@ionic-native/device';
import { ScreenOrientation } from '@ionic-native/screen-orientation';
import { TranslateService } from '@ngx-translate/core';
import { AlertController, Nav, Platform, ViewController } from 'ionic-angular';
import { BaseComponent } from '../core/components/base.component';
import { GuardService } from '../core/services/guard/guard.service';
import { LoginPage } from '../vas/pages/login/login';
import { RootPage } from '../vas/pages/root/root';
import { VasAppFacade } from '../vas/services/vas-app-facade.service';
import { AppConfig } from './app.config';

@Component({
    templateUrl: 'app.html'
})
export class MyApp extends BaseComponent {
    @ViewChild(Nav) nav: Nav;

    rootPage: any = LoginPage;

    _alert;

    constructor(
        protected platform: Platform,
        protected config: AppConfig,
        protected translateService: TranslateService,
        protected appFacade: VasAppFacade,
        protected device: Device,
        protected alertController: AlertController,
        protected guardService: GuardService,
        protected screenOrientation: ScreenOrientation
    ) {
        super();
    }

    ngOnInit(): void {

        (window as any).appFacade = this.appFacade;

        // tslint:disable-next-line:only-arrow-functions
        (window as any).tapElement = function (elem): void {
            const rect = elem.getBoundingClientRect();
            const p = {x: rect.left + rect.width / 2, y: rect.top + rect.height / 2};

            const evStart = document.createEvent('event');
            evStart.initEvent('touchstart', true, true);
            (evStart as any).touches = [
                {identifier: Date.now(), pageX: p.x, pageY: p.y, screenX: p.x, screenY: p.y, clientX: p.x, clientY: p.y}
            ];

            const evEnd = document.createEvent('event');
            evEnd.initEvent('touchend', true, true);
            (evEnd as any).touches = [
                {identifier: Date.now(), pageX: p.x, pageY: p.y, screenX: p.x, screenY: p.y, clientX: p.x, clientY: p.y}
            ];

            elem.dispatchEvent(evStart);
            elem.dispatchEvent(evEnd);
            elem.click();
        };

        this.translateService.setDefaultLang('en-GB');

        this.nav.viewDidEnter
            .takeUntil(this.ngUnsubscribe)
            .subscribe((view: ViewController) => {
                // console.log("viewDidEnter", view.name);
                this.guardService.setCurrentPage(view.name);
            });

        this.nav.viewDidEnter
            .takeUntil(this.ngUnsubscribe)
            .subscribe(() => {
                window.dispatchEvent(new Event('resize'));
            });

        // the guard service will monitor if the user is authorised and will navigate us to the login or accounts page accordingly
        // this.guardService
        //     .getPage()
        //     .subscribe(
        //         page => {
        //             this.nav.setRoot(page);
        //         },
        //         error => console.error(error)
        //     );

        // this function will be called to start the application
        this.appFacade.boot()
            .concatMap(() => this.appFacade.loading.showLoading())
            .concatMap(() => this.appFacade.network.refreshStatus$())
            .do(() => this.appFacade.loading.hideLoading())
            .take(1)
            .subscribe(
                status => {

                    if (status.ip && !status.authenticated) {
                        this.openPage('LoginPage');
                    } else if (status.ip && status.authenticated && !window.location.hash) {
                        this.openPage('AccountsPage');
                    }

                    // this.guardService.init();

                    // @todo WORKAROUND for https://git.ironcode.co.uk/vas/vas-mobile/issues/1
                    // this.initPush();
                });

        this.platform
            .ready()
            .then(() => {
                console.log('Device UUID is %s and isVirtual is', this.device.uuid, this.device.isVirtual);
                this.initKeyboardWatcher();
                this.initBackButtonWatcher();
                this.setupOrientation();
            });
    }

    setupOrientation(): void {
        // @todo it would be nice to lock VAS into portrait but:
        // 1) this doesn't work on android
        // 2) we need to unlock for allow the camera to work properly

        // this.screenOrientation.lock(
        //     this.screenOrientation.ORIENTATIONS.PORTRAIT
        // ).then(
        //     () => {
        //         console.log('screen orientation locked');
        //     },
        //     error => {
        //         console.error(error);
        //     }
        // );
    }

    goToRoot(): void {
        this.nav.setRoot(RootPage).then(
            () => {
                // noop
            },
            error => {
                console.error(error);
            }
        );
    }

    /**
     * Monitor when the keyboard is opened/closed and add a class to the body element so we can react to it.
     * Info:
     * - https://github.com/driftyco/ionic-plugin-keyboard
     * - https://gist.github.com/EvanWillms/8773f8cfa12f469fabf0bdd7a75723e7
     */
    initKeyboardWatcher(): void {
        window.addEventListener('native.keyboardhide', () => {
            document.body.classList.remove('hide-on-keyboard-open');
        });

        window.addEventListener('native.keyboardshow', () => {
            document.body.classList.add('hide-on-keyboard-open');
        });
    }

    initBackButtonWatcher(): void {
        console.log('initBackButtonWatcher');
        this.platform.registerBackButtonAction(() => {

            // uncomment this and comment code below to to show toast and exit app
            // if (this.backButtonPressedOnceToExit) {
            //   this.platform.exitApp();
            // } else if (this.nav.canGoBack()) {
            //   this.nav.pop({});
            // } else {
            //   this.showToast();
            //   this.backButtonPressedOnceToExit = true;
            //   setTimeout(() => {
            //
            //     this.backButtonPressedOnceToExit = false;
            //   },2000)
            // }

            console.log(`backButtonWatcher - ${this.nav.canGoBack() ? '\'can\'' : '\'can\'t\''} go back`);

            return false;
            // if (this.nav.canGoBack()) {
            //     this.nav.pop();
            // } else {
            //     if (this._alert) {
            //         this._alert.dismiss();
            //         this._alert = null;
            //     } else {
            //         this.confirmExit();
            //     }
            // }
        }, 1000);
    }

    confirmExit(): void {
        this._alert = this.alertController.create({
            title: 'Exit?',
            message: 'Are you sure you want to exit the app?',
            buttons: [
                {
                    text: 'Cancel',
                    role: 'cancel',
                    handler: () => {
                        this._alert = undefined;
                    }
                },
                {
                    text: 'Exit',
                    handler: () => {
                        this.platform.exitApp();
                    }
                }
            ]
        });
        this._alert.present();
    }

    initPush(): void {
        // this.pushService.init();
    }

    /**
     * DO NOT CHANGE THIS FUNCTION e.g. rename etc... it will break. Ionic issues.
     * @param page
     */
    openPage(page): void {
        this.nav.setRoot(page);
    }
}



// WEBPACK FOOTER //
// ./src/app/app.component.ts